import { useCallback } from 'react';

import { useSessionStorageValueAsync } from '#src/app/shared/hooks/storage';

type Result = {
	isBannerDismissed: (bannerKey: string) => boolean;
	setBannerDismissed: (bannerKey: string) => void;
	clearBannerDismissed: (bannerKey: string) => void;
	clearAllDismissed: () => void;
};

export function useBannerDismissalStorage(): Result {
	const STORAGE_KEY = 'starterDashboard_dismissedBanners';
	const [dismissedBannersString, setDismissedBannersString] = useSessionStorageValueAsync(STORAGE_KEY);

	const getDismissedBanners = useCallback((): Set<string> => {
		const jsonString =
			dismissedBannersString === undefined ? sessionStorage.getItem(STORAGE_KEY) : dismissedBannersString;

		try {
			const parsed: unknown = jsonString ? JSON.parse(jsonString) : [];
			const bannerArray = Array.isArray(parsed)
				? parsed.filter((item): item is string => typeof item === 'string')
				: [];
			return new Set(bannerArray);
		} catch {
			return new Set();
		}
	}, [dismissedBannersString]);

	const isBannerDismissed = useCallback(
		(bannerKey: string): boolean => {
			return getDismissedBanners().has(bannerKey);
		},
		[getDismissedBanners],
	);

	const setBannerDismissed = useCallback(
		(bannerKey: string) => {
			const dismissedBanners = getDismissedBanners();
			dismissedBanners.add(bannerKey);
			setDismissedBannersString(JSON.stringify([...dismissedBanners]));
		},
		[getDismissedBanners, setDismissedBannersString],
	);

	const clearBannerDismissed = useCallback(
		(bannerKey: string) => {
			const dismissedBanners = getDismissedBanners();
			dismissedBanners.delete(bannerKey);
			setDismissedBannersString(JSON.stringify([...dismissedBanners]));
		},
		[getDismissedBanners, setDismissedBannersString],
	);

	const clearAllDismissed = useCallback(() => {
		setDismissedBannersString(null);
	}, [setDismissedBannersString]);

	return {
		isBannerDismissed,
		setBannerDismissed,
		clearBannerDismissed,
		clearAllDismissed,
	};
}
